/**
 * Widget styles — CSS-in-JS for the Nova chat widget shadow DOM.
 *
 * Design channels: Vercel AI Chat, Linear side panels, Raycast.
 * Never: Bootstrap, generic SaaS, admin-panel aesthetics.
 *
 * All styles are scoped inside a shadow root. Theme customisation is
 * exposed via CSS custom properties on :host, which consumers can
 * override from the light DOM using --nova-theme-* properties.
 *
 * Colour contrast targets: WCAG 2.1 AA (4.5:1 for normal text).
 *   - Light: #1a1a1a on #fafafa = 15.4:1
 *   - Light secondary: #6b6b6b on #fafafa = 4.8:1
 *   - Dark: #ebebeb on #111113 = 14.35:1
 *   - Dark secondary: #9a9a9a on #111113 = 6.7:1
 *   - Brand on white (send btn text): #ffffff on #0066FF = 4.75:1
 */

/** Options for {@link getWidgetStyles}. */
export interface WidgetStyleOptions {
  /** Enable dark colour palette. `'auto'` defers to `prefers-color-scheme`. */
  darkMode?: boolean | 'auto';
  /** CSP nonce to set on the created `<style>` element. */
  csrfNonce?: string;
}

/* ------------------------------------------------------------------ */
/*  Easing tokens                                                     */
/* ------------------------------------------------------------------ */

const SPRING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const EASE_OUT = 'cubic-bezier(0.16, 1, 0.3, 1)';

/* ------------------------------------------------------------------ */
/*  CSS custom properties (light defaults)                            */
/* ------------------------------------------------------------------ */

const CSS_VARIABLES_LIGHT = `
  :host {
    --nova-brand: var(--nova-theme-brand, #0066FF);
    --nova-bg: var(--nova-theme-bg, #fafafa);
    --nova-bg-secondary: var(--nova-theme-bg-secondary, #f2f2f3);
    --nova-text: var(--nova-theme-text, #1a1a1a);
    --nova-text-secondary: var(--nova-theme-text-secondary, #6b6b6b);
    --nova-border: var(--nova-theme-border, rgba(0, 0, 0, 0.08));
    --nova-font: var(--nova-theme-font, inherit);
    --nova-radius: var(--nova-theme-radius, 12px);
    --nova-shadow: var(--nova-theme-shadow,
      0 0 0 1px rgba(0, 0, 0, 0.04),
      0 2px 4px rgba(0, 0, 0, 0.04),
      0 8px 16px rgba(0, 0, 0, 0.06),
      0 24px 48px rgba(0, 0, 0, 0.08)
    );

    font-family: var(--nova-font);
    font-feature-settings: "ss01", "cv01";
    color: var(--nova-text);
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

const CSS_VARIABLES_DARK = `
  :host {
    --nova-brand: var(--nova-theme-brand, #0066FF);
    --nova-bg: var(--nova-theme-bg, #111113);
    --nova-bg-secondary: var(--nova-theme-bg-secondary, #1a1a1c);
    --nova-text: var(--nova-theme-text, #ebebeb);
    --nova-text-secondary: var(--nova-theme-text-secondary, #9a9a9a);
    --nova-border: var(--nova-theme-border, rgba(255, 255, 255, 0.08));
    --nova-font: var(--nova-theme-font, inherit);
    --nova-radius: var(--nova-theme-radius, 12px);
    --nova-shadow: var(--nova-theme-shadow,
      0 0 0 1px rgba(255, 255, 255, 0.06),
      0 2px 4px rgba(0, 0, 0, 0.16),
      0 8px 16px rgba(0, 0, 0, 0.2),
      0 24px 48px rgba(0, 0, 0, 0.28)
    );

    font-family: var(--nova-font);
    font-feature-settings: "ss01", "cv01";
    color: var(--nova-text);
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`;

/* Dark mode overrides for hardcoded (non-variable) colours.
   These MUST be appended AFTER CSS_BASE so they win by source order. */
const CSS_DARK_OVERRIDES = `
  .nova-issue-card__badge--resolved { background: #166534; color: #dcfce7; }
  .nova-issue-card__badge--in_progress,
  .nova-issue-card__badge--executing { background: #92400e; color: #fef3c7; }
  .nova-issue-card__badge--investigating,
  .nova-issue-card__badge--triaging,
  .nova-issue-card__badge--analysing { background: #1e40af; color: #dbeafe; }
  .nova-issue-card__badge--archived,
  .nova-issue-card__badge--dismissed { background: rgba(255,255,255,0.08); color: rgba(255,255,255,0.5); }
  .nova-issue-card__badge--received { background: rgba(99,102,241,0.2); color: #a5b4fc; }
`;

const CSS_VARIABLES_AUTO = `
  ${CSS_VARIABLES_LIGHT}

  @media (prefers-color-scheme: dark) {
    ${CSS_VARIABLES_DARK}
    ${CSS_DARK_OVERRIDES}
  }
`;

/* ------------------------------------------------------------------ */
/*  Base component styles                                             */
/* ------------------------------------------------------------------ */

const CSS_BASE = `
  /* Reset ---------------------------------------------------------- */
  *, *::before, *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* Custom scrollbar ----------------------------------------------- */
  .nova-messages::-webkit-scrollbar {
    width: 4px;
  }

  .nova-messages::-webkit-scrollbar-track {
    background: transparent;
  }

  .nova-messages::-webkit-scrollbar-thumb {
    background: var(--nova-border);
    border-radius: 2px;
  }

  .nova-messages::-webkit-scrollbar-thumb:hover {
    background: var(--nova-text-secondary);
  }

  .nova-messages {
    scrollbar-width: thin;
    scrollbar-color: var(--nova-border) transparent;
  }

  /* Panel ---------------------------------------------------------- */
  .nova-panel {
    position: fixed;
    top: 0;
    right: 0;
    transform: translateX(100%) scale(0.98);
    width: 420px;
    max-width: 100vw;
    height: 100vh;
    max-height: 100vh;
    background: var(--nova-bg);
    border-left: 1px solid var(--nova-border);
    border-radius: 0;
    box-shadow: var(--nova-shadow);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 2147483647;
    opacity: 0;
    transition: transform 0.3s ${SPRING},
                opacity 0.2s ${EASE_OUT};
  }

  .nova-panel--open {
    transform: translateX(0) scale(1);
    opacity: 1;
  }

  /* Header --------------------------------------------------------- */
  .nova-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 16px 20px;
    background: var(--nova-bg);
    border-bottom: 1px solid var(--nova-border);
    color: var(--nova-text);
    flex-shrink: 0;
  }

  .nova-header__icon {
    width: 28px;
    height: 28px;
    flex-shrink: 0;
    color: var(--nova-brand);
  }

  .nova-header__icon svg {
    width: 100%;
    height: 100%;
  }

  .nova-header__title {
    flex: 1;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.01em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: var(--nova-text);
  }

  .nova-header__close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--nova-text-secondary);
    cursor: pointer;
    flex-shrink: 0;
    font-size: 16px;
    line-height: 1;
    transition: background 0.15s ease, color 0.15s ease;
  }

  .nova-header__close:hover {
    background: var(--nova-bg-secondary);
    color: var(--nova-text);
  }

  .nova-header__close:focus-visible {
    outline: 2px solid var(--nova-brand);
    outline-offset: 2px;
  }

  /* Issues accordion ----------------------------------------------- */
  .nova-issues-accordion {
    border-bottom: 1px solid var(--nova-border);
    flex-shrink: 0;
  }

  .nova-issues-accordion__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    cursor: pointer;
    background: none;
    border: none;
    width: 100%;
    text-align: left;
    font: inherit;
    color: var(--nova-text);
    transition: background 150ms;
  }

  .nova-issues-accordion__header:hover {
    background: var(--nova-bg-secondary);
  }

  .nova-issues-accordion__header:focus-visible {
    outline: 2px solid var(--nova-brand);
    outline-offset: -2px;
  }

  .nova-issues-accordion__icon {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    color: var(--nova-text-secondary);
  }

  .nova-issues-accordion__icon svg {
    width: 100%;
    height: 100%;
  }

  .nova-issues-accordion__title {
    flex: 1;
    font-size: 14px;
    font-weight: 600;
  }

  .nova-issues-accordion__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 11px;
    background: var(--nova-bg-secondary);
    font-size: 12px;
    font-weight: 600;
    color: var(--nova-text-secondary);
  }

  .nova-issues-accordion__chevron {
    width: 16px;
    height: 16px;
    transition: transform 200ms;
    color: var(--nova-text-secondary);
  }

  .nova-issues-accordion__chevron--expanded {
    transform: rotate(90deg);
  }

  .nova-issues-accordion__list {
    display: none;
    padding: 0 16px 12px;
  }

  .nova-issues-accordion__list--expanded {
    display: block;
  }

  /* Issue card (expandable) ---------------------------------------- */
  .nova-issue-card {
    border-bottom: 1px solid var(--nova-border);
  }

  .nova-issue-card:last-child {
    border-bottom: none;
  }

  .nova-issue-card__header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 0;
    cursor: pointer;
    background: none;
    border: none;
    width: 100%;
    text-align: left;
    font: inherit;
    color: var(--nova-text);
    transition: opacity 150ms;
  }

  .nova-issue-card__header:hover {
    opacity: 0.8;
  }

  .nova-issue-card__header:focus-visible {
    outline: 2px solid var(--nova-brand);
    outline-offset: 2px;
    border-radius: 6px;
  }

  .nova-issue-card__icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: var(--nova-text-secondary);
  }

  .nova-issue-card__icon svg {
    width: 100%;
    height: 100%;
  }

  .nova-issue-card__summary {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: var(--nova-text);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .nova-issue-card--expanded .nova-issue-card__summary {
    white-space: normal;
    overflow: visible;
  }

  .nova-issue-card__badge {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 500;
    padding: 2px 8px;
    border-radius: 10px;
    white-space: nowrap;
  }

  .nova-issue-card__badge--resolved { background: #dcfce7; color: #166534; }
  .nova-issue-card__badge--in_progress,
  .nova-issue-card__badge--executing { background: #fef3c7; color: #92400e; }
  .nova-issue-card__badge--investigating,
  .nova-issue-card__badge--triaging,
  .nova-issue-card__badge--analysing { background: #dbeafe; color: #1e40af; }
  .nova-issue-card__badge--archived,
  .nova-issue-card__badge--dismissed { background: var(--nova-bg-secondary); color: var(--nova-text-secondary); }
  .nova-issue-card__badge--received { background: rgba(99, 102, 241, 0.1); color: #4f46e5; }

  /* Expandable detail — hidden by default */
  .nova-issue-card__detail {
    display: none;
    padding: 0 0 12px 24px;
    flex-direction: column;
    gap: 8px;
  }

  .nova-issue-card--expanded .nova-issue-card__detail {
    display: flex;
  }

  .nova-issue-card__status-msg {
    font-size: 13px;
    line-height: 1.5;
    color: var(--nova-text);
  }

  .nova-issue-card__meta {
    font-size: 11px;
    color: var(--nova-text-secondary);
  }

  .nova-issue-card__follow-up {
    align-self: flex-start;
    padding: 5px 12px;
    border: 1px solid var(--nova-border);
    border-radius: 8px;
    background: var(--nova-bg);
    color: var(--nova-text);
    font-size: 12px;
    font-family: var(--nova-font);
    cursor: pointer;
    transition: border-color 150ms, background 150ms;
  }

  .nova-issue-card__follow-up:hover {
    border-color: var(--nova-brand);
    background: rgba(0, 102, 255, 0.04);
  }

  .nova-issue-card__follow-up:focus-visible {
    outline: 2px solid var(--nova-brand);
    outline-offset: 2px;
  }

  /* Topic chips ---------------------------------------------------- */
  .nova-topic-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--nova-text-secondary);
    padding: 16px 16px 8px;
  }

  .nova-topic-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    padding: 0 16px 16px;
  }

  .nova-topic-chip {
    display: inline-flex;
    align-items: center;
    padding: 6px 14px;
    border-radius: 20px;
    border: 1px solid var(--nova-border);
    background: var(--nova-bg);
    color: var(--nova-text);
    font-size: 13px;
    font-family: var(--nova-font);
    cursor: pointer;
    transition: background 150ms, border-color 150ms;
  }

  .nova-topic-chip:hover {
    background: var(--nova-bg-secondary);
    border-color: var(--nova-brand);
  }

  .nova-topic-chip:focus-visible {
    outline: 2px solid var(--nova-brand);
    outline-offset: 2px;
  }

  /* Messages ------------------------------------------------------- */
  .nova-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    scroll-behavior: smooth;
    overscroll-behavior: contain;
  }

  .nova-message {
    max-width: 82%;
    padding: 10px 14px;
    border-radius: 16px;
    font-size: 14px;
    line-height: 1.55;
    letter-spacing: -0.006em;
    word-wrap: break-word;
    overflow-wrap: break-word;
    opacity: 0;
    transform: translateY(6px) scale(0.98);
    animation: nova-message-appear 0.3s ${SPRING} forwards;
  }

  .nova-message--user {
    align-self: flex-end;
    background: var(--nova-brand);
    color: #ffffff;
    border-bottom-right-radius: 4px;
  }

  .nova-message--assistant {
    align-self: flex-start;
    background: var(--nova-bg-secondary);
    color: var(--nova-text);
    border: 1px solid var(--nova-border);
    border-bottom-left-radius: 4px;
  }

  /* (Suggested prompts now use .nova-topic-chips above) */

  /* Typing indicator ----------------------------------------------- */
  .nova-typing {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 12px 16px;
    background: var(--nova-bg-secondary);
    border: 1px solid var(--nova-border);
    border-radius: 16px;
    border-bottom-left-radius: 4px;
    align-self: flex-start;
  }

  .nova-typing__dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--nova-text-secondary);
    opacity: 0.6;
    animation: nova-typing-bounce 1.4s ease-in-out infinite;
  }

  .nova-typing__dot:nth-child(2) {
    animation-delay: 0.15s;
  }

  .nova-typing__dot:nth-child(3) {
    animation-delay: 0.3s;
  }

  /* Input area ----------------------------------------------------- */
  .nova-input-area {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 12px 16px 16px;
    border-top: 1px solid var(--nova-border);
    background: var(--nova-bg);
    flex-shrink: 0;
  }

  .nova-input {
    flex: 1;
    min-height: 40px;
    max-height: 120px;
    padding: 9px 12px;
    border: 1px solid var(--nova-border);
    border-radius: 12px;
    background: var(--nova-bg-secondary);
    color: var(--nova-text);
    font-size: 14px;
    font-family: var(--nova-font);
    line-height: 1.5;
    resize: none;
    overflow-y: auto;
    transition: border-color 0.15s ease,
                box-shadow 0.2s ease,
                background 0.15s ease;
  }

  .nova-input::placeholder {
    color: var(--nova-text-secondary);
  }

  .nova-input:focus {
    outline: none;
    background: var(--nova-bg);
    border-color: var(--nova-brand);
    box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1),
                0 1px 2px rgba(0, 0, 0, 0.04);
  }

  .nova-send-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 10px;
    background: var(--nova-brand);
    color: #ffffff;
    cursor: pointer;
    flex-shrink: 0;
    margin-bottom: 4px;
    transition: opacity 0.15s ease,
                transform 0.15s ${SPRING},
                box-shadow 0.15s ease;
  }

  .nova-send-btn:hover:not(:disabled) {
    transform: scale(1.06);
    box-shadow: 0 2px 8px rgba(0, 102, 255, 0.25);
  }

  .nova-send-btn:active:not(:disabled) {
    transform: scale(0.94);
  }

  .nova-send-btn:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  .nova-send-btn:focus-visible {
    outline: 2px solid var(--nova-brand);
    outline-offset: 2px;
  }

  .nova-send-btn svg {
    width: 15px;
    height: 15px;
  }

  .nova-attach-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: none;
    border-radius: 10px;
    background: transparent;
    color: var(--nova-text-secondary);
    cursor: pointer;
    flex-shrink: 0;
    margin-bottom: 4px;
    transition: color 0.15s ease, background 0.15s ease;
  }

  .nova-attach-btn:hover {
    background: var(--nova-bg-secondary);
    color: var(--nova-text);
  }

  .nova-attach-btn:focus-visible {
    outline: 2px solid var(--nova-brand);
    outline-offset: 2px;
  }

  .nova-attach-btn svg {
    width: 16px;
    height: 16px;
  }

  /* (Issue cards replaced by .nova-issue-row above) */

  /* Updates banner ------------------------------------------------- */
  .nova-updates-banner {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: rgba(0, 102, 255, 0.06);
    color: var(--nova-brand);
    font-size: 13px;
    font-weight: 500;
    flex-shrink: 0;
    border-bottom: 1px solid var(--nova-border);
  }

  .nova-updates-banner__icon {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }

  /* Privacy notice ------------------------------------------------- */
  .nova-privacy-notice {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 20px;
    background: var(--nova-bg-secondary);
    font-size: 12px;
    color: var(--nova-text-secondary);
    line-height: 1.5;
    flex-shrink: 0;
  }

  .nova-privacy-notice__dismiss {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: var(--nova-text-secondary);
    cursor: pointer;
    flex-shrink: 0;
    font-size: 14px;
    line-height: 1;
    transition: color 0.15s ease;
  }

  .nova-privacy-notice__dismiss:hover {
    color: var(--nova-text);
  }

  .nova-privacy-notice__dismiss:focus-visible {
    outline: 2px solid var(--nova-brand);
    outline-offset: 2px;
  }

  .nova-privacy-notice__text {
    flex: 1;
  }

  /* Empty state ---------------------------------------------------- */
  .nova-empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 32px 24px;
    text-align: center;
    color: var(--nova-text-secondary);
    font-size: 14px;
    line-height: 1.5;
  }

  .nova-empty-state__icon {
    width: 48px;
    height: 48px;
    margin-bottom: 12px;
    opacity: 0.4;
  }

  /* Loading / skeleton --------------------------------------------- */
  .nova-loading {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 20px;
    flex: 1;
  }

  .nova-loading__bar {
    height: 12px;
    border-radius: 6px;
    background: var(--nova-bg-secondary);
    animation: nova-pulse 1.5s ease-in-out infinite;
  }

  .nova-loading__bar--short {
    width: 60%;
  }

  .nova-loading__bar--medium {
    width: 80%;
  }

  .nova-loading__bar--long {
    width: 100%;
  }

  /* Feedback buttons (resolution confirmation) --------------------- */
  .nova-feedback {
    display: flex;
    gap: 8px;
    margin-top: 8px;
  }

  .nova-feedback__btn {
    padding: 6px 12px;
    border: 1px solid var(--nova-border);
    border-radius: 8px;
    background: var(--nova-bg);
    color: var(--nova-text);
    font-size: 13px;
    font-family: var(--nova-font);
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .nova-feedback__btn:hover {
    border-color: var(--nova-brand);
    background: rgba(0, 102, 255, 0.05);
  }

  .nova-feedback__btn:focus-visible {
    outline: 2px solid var(--nova-brand);
    outline-offset: 2px;
  }

  .nova-feedback__btn--positive {
    color: #059669;
    border-color: rgba(16, 185, 129, 0.2);
    background: rgba(16, 185, 129, 0.06);
  }

  .nova-feedback__btn--positive:hover {
    background: rgba(16, 185, 129, 0.12);
    border-color: rgba(16, 185, 129, 0.3);
  }

  .nova-feedback__btn--negative {
    color: #dc2626;
    border-color: rgba(220, 38, 38, 0.2);
    background: rgba(220, 38, 38, 0.06);
  }

  .nova-feedback__btn--negative:hover {
    background: rgba(220, 38, 38, 0.12);
    border-color: rgba(220, 38, 38, 0.3);
  }

  /* Backdrop (click-outside-to-close) ------------------------------- */
  .nova-backdrop {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2147483646;
    background: rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(2px);
    -webkit-backdrop-filter: blur(2px);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.3s ${EASE_OUT};
  }

  .nova-backdrop--visible {
    opacity: 1;
    pointer-events: auto;
  }

  /* Screen-reader only --------------------------------------------- */
  .nova-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
`;

/* ------------------------------------------------------------------ */
/*  Animations                                                        */
/* ------------------------------------------------------------------ */

const CSS_ANIMATIONS = `
  @keyframes nova-message-appear {
    from {
      opacity: 0;
      transform: translateY(6px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @keyframes nova-typing-bounce {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: 0.4;
    }
    30% {
      transform: translateY(-3px);
      opacity: 1;
    }
  }

  @keyframes nova-pulse {
    0%, 100% {
      opacity: 0.5;
    }
    50% {
      opacity: 0.2;
    }
  }
`;

/* ------------------------------------------------------------------ */
/*  Reduced motion override                                           */
/* ------------------------------------------------------------------ */

const CSS_REDUCED_MOTION = `
  @media (prefers-reduced-motion: reduce) {
    .nova-panel {
      transition: none;
    }

    .nova-message {
      animation: none;
      opacity: 1;
      transform: none;
    }

    .nova-typing__dot {
      animation: none;
    }

    .nova-loading__bar {
      animation: none;
    }

    .nova-send-btn,
    .nova-topic-chip,
    .nova-issues-accordion__chevron {
      transition: none;
    }

    .nova-backdrop {
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
    }
  }
`;

/* ------------------------------------------------------------------ */
/*  Responsive (full-screen on small viewports)                       */
/* ------------------------------------------------------------------ */

const CSS_RESPONSIVE = `
  /* Tablet: constrain width, keep slide-in */
  @media (max-width: 820px) and (min-width: 481px) {
    .nova-panel {
      width: min(380px, 85vw);
    }
  }

  /* Mobile: full-screen takeover */
  @media (max-width: 480px) {
    .nova-panel {
      width: 100vw;
      height: 100vh;
      max-height: 100vh;
      top: 0;
      right: 0;
      border-radius: 0;
      border-left: none;
      transform: translateX(100%) scale(1);
    }

    .nova-panel--open {
      transform: translateX(0) scale(1);
    }
  }
`;

/* ------------------------------------------------------------------ */
/*  Public API                                                        */
/* ------------------------------------------------------------------ */

/**
 * Build the complete CSS string for the Nova chat widget shadow DOM.
 *
 * @param options.darkMode - `true` forces dark palette, `'auto'` adds a
 *   `prefers-color-scheme: dark` media query, `false`/`undefined` uses light.
 * @param options.csrfNonce - CSP nonce (only used by {@link createStyleElement}).
 * @returns A single CSS string suitable for injection into a shadow root.
 */
export function getWidgetStyles(options?: WidgetStyleOptions): string {
  const darkMode = options?.darkMode;

  let variables: string;
  if (darkMode === true) {
    variables = CSS_VARIABLES_DARK;
  } else if (darkMode === 'auto') {
    variables = CSS_VARIABLES_AUTO;
  } else {
    variables = CSS_VARIABLES_LIGHT;
  }

  const parts = [variables, CSS_BASE];

  // Dark overrides must come AFTER CSS_BASE so they win by source order
  if (darkMode === true) {
    parts.push(CSS_DARK_OVERRIDES);
  }

  parts.push(CSS_ANIMATIONS, CSS_REDUCED_MOTION, CSS_RESPONSIVE);

  return parts.join('\n');
}

/**
 * Create a `<style>` element containing the widget CSS.
 *
 * @param styles - CSS string (typically from {@link getWidgetStyles}).
 * @param nonce  - Optional CSP nonce to set on the element.
 * @returns A ready-to-append `<style>` element.
 */
export function createStyleElement(styles: string, nonce?: string): HTMLStyleElement {
  const el = document.createElement('style');
  if (nonce) {
    el.setAttribute('nonce', nonce);
  }
  el.textContent = styles;
  return el;
}
